// covered-by: frontends/carrie/src/routes/ask-puntt/hooks/__tests__/useChatArtifactWorkspace.test.ts
import { useCallback, useEffect, useMemo, useState } from "react";

import {
  type ChatArtifact,
  type ChatArtifactOpenPayload,
  type ChatArtifactSelection,
} from "../artifactTypes";
import {
  collectChatArtifacts,
  type CollectChatArtifactsMessage,
  type ReviewSummaryForArtifact,
} from "../chatUtils";
import { type CanvasContent } from "../components/canvas/types";

export type UseChatArtifactWorkspaceArgs = {
  messages: readonly CollectChatArtifactsMessage[];
  titleMap?: ReadonlyMap<string, string>;
  reviewSummaries?: ReadonlyMap<string, ReviewSummaryForArtifact>;
  chatId?: string;
  onArtifactOpened?: (
    payload: ChatArtifactOpenPayload,
    source: "sidebar" | "message_chip",
  ) => void;
};

export type ActiveCanvasPayload = {
  canvas: CanvasContent & { changedSectionIds?: string[] };
  skill?: string;
  messageId: string;
} | null;

export type UseChatArtifactWorkspaceResult = {
  artifacts: ChatArtifact[];
  reviewArtifacts: ChatArtifact[];
  canvasArtifacts: ChatArtifact[];
  selection: ChatArtifactSelection | null;
  hasSelection: boolean;
  activeViewTicketId: string | null;
  activeCanvas: ActiveCanvasPayload;
  openArtifact: (
    payload: ChatArtifactOpenPayload,
    source?: "sidebar" | "message_chip",
  ) => void;
  closeArtifact: () => void;
  isArtifactActive: (artifact: ChatArtifact) => boolean;
};

function findMessageById(
  messages: readonly CollectChatArtifactsMessage[],
  messageId: string,
): CollectChatArtifactsMessage | undefined {
  return messages.find((m) => m._id === messageId);
}

export function useChatArtifactWorkspace(
  args: UseChatArtifactWorkspaceArgs,
): UseChatArtifactWorkspaceResult {
  const {
    messages,
    titleMap = new Map(),
    reviewSummaries = new Map(),
    chatId,
    onArtifactOpened,
  } = args;

  const [selection, setSelection] = useState<ChatArtifactSelection | null>(
    null,
  );

  const artifacts = useMemo(
    () => collectChatArtifacts(messages, titleMap, reviewSummaries),
    [messages, titleMap, reviewSummaries],
  );

  const reviewArtifacts = useMemo(
    () => artifacts.filter((a) => a.kind === "review"),
    [artifacts],
  );

  const canvasArtifacts = useMemo(
    () => artifacts.filter((a) => a.kind === "canvas"),
    [artifacts],
  );

  const openArtifact = useCallback(
    (
      payload: ChatArtifactOpenPayload,
      source: "sidebar" | "message_chip" = "sidebar",
    ) => {
      if (payload.kind === "askPunttReview") {
        return;
      }
      if (payload.kind === "review") {
        const artifact = reviewArtifacts.find(
          (a) => a.ticketId === payload.ticketId,
        );
        // Reject only when the rail already materialized and this ticket is
        // not in it. An empty list is the cold deep-link race (chatData has
        // viewTicketId before localMessages/collectChatArtifacts catch up).
        //
        // Do not block `isGenerating` either: rail cards disable clicks
        // (ArtifactListCard), but shareable `/reviews/:ticketId` deep links
        // call this during first-review limbo and must hydrate the viewer.
        // Otherwise openStandaloneReview clears browse mode while selection
        // stays null → chat-only with no rail (the nightly failure mode).
        //
        // (Fingerprint bump: epic deploy-meta had already stamped this FE
        // hash without shipping the bundle — keep this comment so decide
        // redeploys Carrie.)
        if (!artifact && reviewArtifacts.length > 0) return;
        setSelection({ kind: "review", ticketId: payload.ticketId });
      } else if (payload.kind === "canvas") {
        const exists = canvasArtifacts.some(
          (a) => a.messageId === payload.messageId,
        );
        if (!exists) return;
        setSelection({ kind: "canvas", messageId: payload.messageId });
      }
      onArtifactOpened?.(payload, source);
    },
    [reviewArtifacts, canvasArtifacts, onArtifactOpened],
  );

  const closeArtifact = useCallback(() => {
    setSelection(null);
  }, []);

  const isArtifactActive = useCallback(
    (artifact: ChatArtifact): boolean => {
      if (!selection) return false;
      if (selection.kind === "review" && artifact.kind === "review") {
        return selection.ticketId === artifact.ticketId;
      }
      if (selection.kind === "canvas" && artifact.kind === "canvas") {
        return selection.messageId === artifact.messageId;
      }
      return false;
    },
    [selection],
  );

  const activeViewTicketId = useMemo((): string | null => {
    if (selection?.kind === "review") return selection.ticketId;
    return null;
  }, [selection]);

  const activeCanvas = useMemo((): ActiveCanvasPayload => {
    if (selection?.kind !== "canvas") return null;
    const msg = findMessageById(messages, selection.messageId);
    if (
      !msg ||
      !msg.canvasContent ||
      !Array.isArray(msg.canvasContent.blocks) ||
      msg.canvasContent.blocks.length === 0
    ) {
      return null;
    }
    return {
      canvas: {
        ...(msg.canvasContent as CanvasContent),
        changedSectionIds: (msg as { canvasChangedSectionIds?: string[] })
          .canvasChangedSectionIds,
      },
      skill: msg.activeSkill ?? undefined,
      messageId: msg._id,
    };
  }, [selection, messages]);

  // Clear selection when chat changes
  useEffect(() => {
    setSelection(null);
  }, [chatId]);

  return {
    artifacts,
    reviewArtifacts,
    canvasArtifacts,
    selection,
    hasSelection: selection != null,
    activeViewTicketId,
    activeCanvas,
    openArtifact,
    closeArtifact,
    isArtifactActive,
  };
}
